import type {FC} from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {styled} from 'styled-components';

import type {NzbCheckStats, NzbMediaMetadataError} from '@repo/shared/api/website_api';
import {addNzbCheckStats, createEmptyNzbCheckStats} from '@repo/shared/lib/nzb_check_stats';

import {Button, ButtonAsLink} from '@repo/shared-web/components/core/button';
import {LoadingIndicator} from '@repo/shared-web/components/core/loading_indicator';

import {apiCall} from '@repo/website-frontend/lib/api';

interface AdminPageProps {}

interface StatsProgress {
  stats: NzbCheckStats;
  mediaMetadataErrors: NzbMediaMetadataError[];
  asOf: number | undefined;
  paginationToken: string | undefined;
}

interface StatsSnapshot {
  stats: NzbCheckStats;
  mediaMetadataErrors: NzbMediaMetadataError[];
  loadedAt: number;
}

type LoadState = 'idle' | 'loading' | 'paused' | 'error' | 'loaded';
const numberFormat = new Intl.NumberFormat();
const rows = [
  {key: 'upToDate', label: 'Up to date'},
  {key: 'neverChecked', label: 'Never checked'},
  {key: 'needsRecheck', label: 'Needs rechecking'},
] as const;

export const AdminPage: FC<AdminPageProps> = () => {
  const [snapshot, setSnapshot] = useState<StatsSnapshot>();
  const [state, setState] = useState<LoadState>('idle');
  const [counted, setCounted] = useState(0);
  const [error, setError] = useState<string>();
  const [errorPage, setErrorPage] = useState(0);
  const progressRef = useRef<StatsProgress>();
  const runRef = useRef(0);
  const loading = state === 'loading';

  useEffect(
    () => () => {
      runRef.current++;
    },
    [],
  );

  const handleLoad = useCallback(async () => {
    const run = ++runRef.current;
    let progress = progressRef.current ?? {
      stats: createEmptyNzbCheckStats(),
      mediaMetadataErrors: [],
      asOf: undefined,
      paginationToken: undefined,
    };
    setCounted(progress.stats.all.total);
    setError(undefined);
    setState('loading');
    try {
      while (true) {
        const page = await apiCall('POST /get-admin-nzb-check-stats', {
          asOf: progress.asOf,
          paginationToken: progress.paginationToken,
        });
        if (runRef.current !== run) return;
        progress = {
          stats: addNzbCheckStats(progress.stats, page.stats),
          mediaMetadataErrors: [...progress.mediaMetadataErrors, ...page.mediaMetadataErrors],
          asOf: page.asOf,
          paginationToken: page.paginationToken,
        };
        progressRef.current = progress;
        setCounted(progress.stats.all.total);
        if (page.paginationToken === undefined) {
          setSnapshot({
            stats: progress.stats,
            mediaMetadataErrors: progress.mediaMetadataErrors,
            loadedAt: Date.now(),
          });
          setErrorPage(0);
          progressRef.current = undefined;
          setState('loaded');
          return;
        }
      }
    } catch (err: unknown) {
      if (runRef.current !== run) return;
      setError(err instanceof Error ? err.message : 'Could not load stats');
      setState('error');
    }
  }, []);

  const handleStop = useCallback(() => {
    runRef.current++;
    setState('paused');
  }, []);

  const handleRestart = useCallback(async () => {
    progressRef.current = undefined;
    await handleLoad();
  }, [handleLoad]);
  const recentMetadataErrors = useMemo(
    () =>
      snapshot?.mediaMetadataErrors
        .slice()
        .sort((a, b) => b.ts - a.ts)
        .slice(errorPage * 10, errorPage * 10 + 10) ?? [],
    [errorPage, snapshot],
  );
  const metadataErrorReasons = useMemo(() => {
    const counts = new Map<string, {count: number; reason: string | undefined}>();
    for (const metadataError of snapshot?.mediaMetadataErrors ?? []) {
      const code = metadataError.errorCode ?? metadataError.reason ?? 'unknown';
      const existing = counts.get(code);
      if (existing) {
        existing.count++;
      } else {
        counts.set(code, {count: 1, reason: metadataError.reason});
      }
    }
    return Array.from(counts, ([code, {count, reason}]) => ({code, count, reason}))
      .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code))
      .slice(0, 10);
  }, [snapshot]);
  const errorCount = snapshot?.mediaMetadataErrors.length ?? 0;
  const errorPageCount = Math.max(1, Math.ceil(errorCount / 10));

  return (
    <Wrapper>
      <h1>Admin</h1>
      <Section aria-labelledby="admin-nzb-checks">
        <SectionHeader>
          <h2 id="admin-nzb-checks">NZB checks</h2>
          <Actions>
            <Button onClickAsync={handleLoad} disabled={loading}>
              {loading
                ? 'Loading stats…'
                : state === 'paused' || state === 'error'
                  ? 'Resume stats'
                  : snapshot
                    ? 'Refresh stats'
                    : 'Load stats'}
            </Button>
            {loading ? <ButtonAsLink onClick={handleStop}>Stop</ButtonAsLink> : null}
            {(state === 'paused' || state === 'error') && counted > 0 ? (
              <ButtonAsLink onClickAsync={handleRestart}>Start over</ButtonAsLink>
            ) : null}
          </Actions>
        </SectionHeader>

        {loading || state === 'paused' || state === 'error' ? (
          <Progress role="status" aria-live="polite">
            {loading ? <LoadingIndicator size={18} /> : null}
            <span>
              {state === 'paused' ? 'Paused · ' : state === 'error' ? 'Stopped · ' : ''}
              {numberFormat.format(counted)} NZBs counted{loading ? '…' : ''}
            </span>
          </Progress>
        ) : null}
        {error ? <ErrorMessage role="alert">{error}</ErrorMessage> : null}

        {snapshot ? (
          <>
            <TableScroll aria-busy={loading}>
              <StatsTable>
                <caption>NZBs in the catalog, grouped by their latest check</caption>
                <thead>
                  <tr>
                    <th scope="col">Status</th>
                    <th scope="col">NZBs</th>
                    <th scope="col">Healthy</th>
                    <th scope="col">Unhealthy</th>
                    <th scope="col" title="Never checked, failed, or no conclusive sample">
                      Unknown
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(({key, label}) => (
                    <tr key={key}>
                      <th scope="row">{label}</th>
                      <td>{numberFormat.format(snapshot.stats[key].total)}</td>
                      <Healthy>{numberFormat.format(snapshot.stats[key].healthy)}</Healthy>
                      <Unhealthy>{numberFormat.format(snapshot.stats[key].unhealthy)}</Unhealthy>
                      <Unknown>{numberFormat.format(snapshot.stats[key].unknown)}</Unknown>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">All NZBs</th>
                    <td>{numberFormat.format(snapshot.stats.all.total)}</td>
                    <Healthy>{numberFormat.format(snapshot.stats.all.healthy)}</Healthy>
                    <Unhealthy>{numberFormat.format(snapshot.stats.all.unhealthy)}</Unhealthy>
                    <Unknown>{numberFormat.format(snapshot.stats.all.unknown)}</Unknown>
                  </tr>
                </tfoot>
              </StatsTable>
            </TableScroll>
            <Metadata>Updated {new Date(snapshot.loadedAt).toLocaleString()}</Metadata>
          </>
        ) : null}
      </Section>
      {snapshot ? (
        <Section aria-labelledby="admin-track-metadata-errors">
          <SectionHeader>
            <h2 id="admin-track-metadata-errors">Track metadata errors</h2>
            <ErrorTotal>{numberFormat.format(errorCount)} errors</ErrorTotal>
          </SectionHeader>
          <SectionDescription>
            Failed metadata lookups from the latest check for each NZB in the catalog.
          </SectionDescription>

          <Subheading>Most common reasons</Subheading>
          {metadataErrorReasons.length > 0 ? (
            <TableScroll>
              <StatsTable>
                <caption>Ten most common track metadata error codes</caption>
                <thead>
                  <tr>
                    <th scope="col">Error code</th>
                    <th scope="col">Count</th>
                    <th scope="col">Example reason</th>
                  </tr>
                </thead>
                <tbody>
                  {metadataErrorReasons.map(({code, count, reason}) => (
                    <tr key={code}>
                      <th scope="row">{code}</th>
                      <td>{numberFormat.format(count)}</td>
                      <ReasonCell>{reason ?? 'No additional details'}</ReasonCell>
                    </tr>
                  ))}
                </tbody>
              </StatsTable>
            </TableScroll>
          ) : (
            <EmptyState>No failed track metadata lookups were found.</EmptyState>
          )}

          <Subheading>Recent errors</Subheading>
          {errorCount > 0 ? (
            <>
              <TableScroll>
                <MetadataErrorTable>
                  <caption>Track metadata errors ordered by check time, newest first</caption>
                  <thead>
                    <tr>
                      <th scope="col">Release</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentMetadataErrors.map(metadataError => (
                      <tr key={`${metadataError.nzbId}-${metadataError.ts}`}>
                        <td>
                          <ReleaseTitle>{metadataError.title}</ReleaseTitle>
                          <ReleaseId>{metadataError.nzbId}</ReleaseId>
                        </td>
                        <ReasonCell>
                          <ErrorCode>{metadataError.errorCode ?? 'unknown'}</ErrorCode>
                          <ReasonText>{metadataError.reason ?? 'No additional details'}</ReasonText>
                        </ReasonCell>
                        <td>{new Date(metadataError.ts).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </MetadataErrorTable>
              </TableScroll>
              <Pager>
                <span>
                  Page {errorPage + 1} of {errorPageCount}
                </span>
                <PagerActions>
                  <ButtonAsLink
                    onClick={() => setErrorPage(page => Math.max(0, page - 1))}
                    disabled={errorPage === 0}
                  >
                    Previous
                  </ButtonAsLink>
                  <ButtonAsLink
                    onClick={() => setErrorPage(page => Math.min(errorPageCount - 1, page + 1))}
                    disabled={errorPage >= errorPageCount - 1}
                  >
                    Next
                  </ButtonAsLink>
                </PagerActions>
              </Pager>
            </>
          ) : (
            <EmptyState>No recent errors to display.</EmptyState>
          )}
        </Section>
      ) : null}
    </Wrapper>
  );
};
AdminPage.displayName = 'AdminPage';

const Wrapper = styled.div`
  width: 100%;
  max-width: 1120px;
  box-sizing: border-box;
  padding: 24px;
  margin: 0 auto;
  h1 {
    font-size: 26px;
    margin: 0 0 20px;
  }
  > section + section {
    margin-top: 16px;
  }
  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const Section = styled.section`
  background: #1b202a;
  border: 1px solid #ffffff12;
  border-radius: 8px;
  padding: 20px;
  min-width: 0;
`;

const SectionDescription = styled.p`
  margin: -4px 0 20px;
  color: #ffffff99;
  font-size: 14px;
`;

const Subheading = styled.h3`
  font-size: 15px;
  margin: 20px 0 8px;
`;

const ErrorTotal = styled.span`
  color: #ff9c96;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  h2 {
    font-size: 18px;
    margin: 0;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const Progress = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffffbb;
  font-size: 14px;
  margin: 12px 0;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const StatsTable = styled.table`
  border-collapse: collapse;
  table-layout: auto;
  min-width: 100%;
  width: max-content;
  font-variant-numeric: tabular-nums;
  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  th,
  td {
    padding: 9px 14px;
    white-space: nowrap;
    text-align: right;
    border-bottom: 1px solid #ffffff12;
  }
  th:first-child {
    text-align: left;
    padding-left: 0;
  }
  th:last-child,
  td:last-child {
    padding-right: 0;
  }
  thead th {
    color: #ffffff88;
    font-size: 13px;
    font-weight: 500;
  }
  tbody th {
    font-weight: 400;
  }
  tfoot th,
  tfoot td {
    padding-top: 13px;
    font-weight: 600;
    border-bottom: 0;
  }
`;

const MetadataErrorTable = styled(StatsTable)`
  th:nth-child(2) {
    text-align: left;
  }
  td:first-child {
    text-align: left;
    padding-left: 0;
  }
  td:nth-child(2) {
    min-width: 260px;
    max-width: 440px;
    white-space: normal;
    text-align: left;
  }
`;

const ReasonCell = styled.td`
  white-space: normal !important;
  text-align: left !important;
`;

const ErrorCode = styled.strong`
  display: block;
  color: #ffb3ad;
  font-weight: 600;
`;

const ReasonText = styled.span`
  display: block;
  color: #ffffffaa;
  max-width: 440px;
`;

const ReleaseTitle = styled.strong`
  display: block;
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReleaseId = styled.span`
  display: block;
  color: #ffffff77;
  font-size: 11px;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  color: #ffffff99;
  font-size: 13px;
`;

const PagerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EmptyState = styled.p`
  margin: 8px 0 16px;
  color: #ffffff88;
  font-size: 14px;
`;

const Healthy = styled.td`
  color: #9ee6ae;
`;

const Unhealthy = styled.td`
  color: #ff9c96;
`;

const Unknown = styled.td`
  color: #ffffff88;
`;

const Metadata = styled.div`
  margin-top: 12px;
  color: #ffffff77;
  font-size: 12px;
`;

const ErrorMessage = styled.div`
  color: #ff9c96;
  font-size: 14px;
  margin: 12px 0;
`;
